import { FC, useEffect, useState, useCallback, useMemo, useRef, createElement } from 'react';
import {
    AppShell,
    Burger,
    Button,
    Group,
    Modal,
    Badge,
    Text,
    Box,
    Card,
    Stack,
    RingProgress,
    SimpleGrid,
    Grid,
    SegmentedControl,
    Menu,
    Avatar,
    Divider,
    TextInput,
    PasswordInput,
    FileButton,
    ActionIcon,
    Paper,
    LoadingOverlay,
    Drawer,
    UnstyledButton,
    Switch,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
    IconCheck,
    IconChevronRight,
    IconCircleCheck,
    IconCircleX,
    IconCpu,
    IconDeviceDesktop,
    IconInfoCircle,
    IconLogout,
    IconMoon,
    IconRefresh,
    IconServer,
    IconSun,
    IconWifi,
    IconX,
    IconVolume,
    IconVolumeOff,
    IconUser,
    IconKey,
    IconUpload,
    IconLock,
} from '@tabler/icons-react';
import Sidebar from '@/shared/components/sidebar/sidebar';
import { useGetFrontendVersion, useGetHealthStatus } from '@/core/api/query/health.query';
import { useCompanySettings } from '@/shared/hooks/useCompanySettings';
import useAuth, { useAuthStore } from '@/core/services/auth/auth';
import { Breadcrumbs } from '@/shared/components/breadcrumbs/breadcrumbs';
import { env } from '@/config/env';
import { useTheme } from '@/core/providers/theme.provider';
import { playAlertSound } from '@/shared/utils/audio/alertSounds';
import { authService } from '@/core/api/auth/auth.service';
import { handleNotification } from '@/shared/components/notification/handle-notification';
import { usePageTitleStore } from '@/core/stores/page-title.store';

// Types
interface DashboardPageProps {
    children?: React.ReactNode;
}

type ServiceStatusEntry = { status?: string | null };
type ServiceStatusMap = Record<string, ServiceStatusEntry>;
type ResourceUsageDetails = { totalGB?: number; usedGB?: number };

type HealthApiPayload = {
    serverDetails?: Record<string, unknown>;
    djangoDetails?: { databaseName?: string } & Record<string, unknown>;
    serviceStatus?: ServiceStatusMap;
    processInfo?: Record<string, unknown>;
    systemLoad?: Record<string, unknown>;
    memoryDetails?: ResourceUsageDetails;
    diskDetails?: ResourceUsageDetails;
};

type DeploymentInfo = {
    isDevelopmentServer: boolean;
    memoryUsagePercent: number;
    diskUsagePercent: number;
    healthyServices: number;
    totalServices: number;
    serverDetails: Record<string, unknown>;
    djangoDetails: Record<string, unknown>;
    serviceStatus: ServiceStatusMap;
    processInfo: Record<string, unknown>;
    systemLoad: Record<string, unknown>;
    memoryDetails: ResourceUsageDetails;
    diskDetails: ResourceUsageDetails;
};

// Constants
const UPDATE_REMINDER_DELAY = 60 * 60 * 1000;
const UPDATE_REMINDER_STORAGE_KEY = 'dashboard_update_reminder';

// Helper Components
const SystemCard = ({
    icon: iconComponent,
    title,
    children,
    color = 'blue',
}: {
    icon: React.ElementType;
    title: string;
    children: React.ReactNode;
    color?: string;
}) => {
    const Icon = iconComponent;
    return (
        <Card withBorder p="md" radius="md" shadow="sm">
            <Group gap="sm" mb="md">
                <div className={`p-2 bg-linear-to-r from-${color}-500 to-${color}-600 rounded-lg`}>
                    <Icon className="w-4 h-4 text-white" />
                </div>
                <Text size="sm" fw={700}>{title}</Text>
            </Group>
            {children}
        </Card>
    );
};

const DashboardPage: FC<DashboardPageProps> = ({ children }) => {
    // Hooks
    const companySettings = useCompanySettings();
    const { logout, authState } = useAuth();
    const baseFileUrl = useMemo(() => {
        return env.baseFileUrl.endsWith('/') ? env.baseFileUrl : `${env.baseFileUrl}/`;
    }, []);
    const userAvatarUrl = useMemo(() => {
        const fileImage = authState?.user?.fileImage;
        if (fileImage && typeof fileImage === 'string' && fileImage.trim()) {
            return fileImage.startsWith('http') ? fileImage : baseFileUrl + fileImage;
        }
        return null;
    }, [authState?.user?.fileImage, baseFileUrl]);
    const { setColorScheme, colorSchemePreference } = useTheme();
    const isMobile = useMediaQuery('(max-width: 48em)') ?? false;
    const pageTitle = usePageTitleStore((st) => st.title);
    const pageTitleIcon = usePageTitleStore((st) => st.icon);
    const [profileSheetOpened, { open: openProfileSheet, close: closeProfileSheet }] = useDisclosure(false);
    const sheetTouchStartY = useRef(0);
    const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);
    const [updateModalOpened, { open: openUpdateModal, close: closeUpdateModal }] = useDisclosure(false);
    const [updateModalView, setUpdateModalView] = useState<'updates' | 'system'>('updates');

    const updateUserStore = useAuthStore((state) => state.updateUser);
    const [profileModalOpened, { open: openProfileModal, close: closeProfileModal }] = useDisclosure(false);

    const isIncompleteProfile = useMemo(() => {
        return !!(authState.isAuthenticated && authState.user && !authState.user.profileCompleted);
    }, [authState.isAuthenticated, authState.user]);

    useEffect(() => {
        if (isIncompleteProfile) {
            openProfileModal();
        }
    }, [isIncompleteProfile, openProfileModal]);

    const [soundEnabled, setSoundEnabled] = useState(() => {
        return localStorage.getItem('task-audio-enabled') !== 'false';
    });

    const toggleSound = () => {
        const nextState = !soundEnabled;
        setSoundEnabled(nextState);
        localStorage.setItem('task-audio-enabled', String(nextState));
        if (nextState) {
            void playAlertSound('notification');
        }
    };

    // API queries
    const { data: healthData, error: healthError, isLoading: healthLoading } = useGetHealthStatus();
    const { data: frontendVersion, refetch: refetchFrontendVersion, isLoading: versionLoading } = useGetFrontendVersion();

    // Memoized values
    const currentVersion = useMemo(() => env.version.toString(), []);
    const updateInfo: { isAvailable: boolean; currentVersion: string; newVersion?: string | null; } = useMemo(() => ({
        isAvailable: Boolean(frontendVersion && frontendVersion.toString() !== currentVersion),
        currentVersion,
        newVersion: frontendVersion?.toString(),
    }), [frontendVersion, currentVersion]);


    const companyName = useMemo(() => companySettings?.data?.description || '', [companySettings?.data?.description]);

    // Deployment info calculations
    const deploymentInfo: DeploymentInfo | null = useMemo(() => {
        if (!healthData?.data || healthError) return null;

        try {
            const {
                serverDetails,
                djangoDetails,
                serviceStatus,
                processInfo,
                systemLoad,
                memoryDetails,
                diskDetails,
            } = healthData.data as unknown as HealthApiPayload;

            if (!djangoDetails || !serviceStatus || !memoryDetails || !diskDetails) {
                return null;
            }

            const serviceStatusMap = serviceStatus ?? {};
            const isDevelopmentServer = djangoDetails?.databaseName !== 'DB_OCIUZ_REDKNOX_LIVE';
            const memoryUsagePercent =
                memoryDetails?.totalGB && memoryDetails?.usedGB
                    ? Math.round((memoryDetails.usedGB / memoryDetails.totalGB) * 100)
                    : 0;
            const diskUsagePercent =
                diskDetails?.totalGB && diskDetails?.usedGB
                    ? Math.round((diskDetails.usedGB / diskDetails.totalGB) * 100)
                    : 0;
            const healthyServices =
                Object.values(serviceStatusMap).filter(
                    status => status?.status?.toLowerCase() === 'healthy' || status?.status?.toLowerCase() === 'ok',
                ).length || 0;
            const totalServices = Object.keys(serviceStatusMap).length;

            return {
                isDevelopmentServer,
                memoryUsagePercent,
                diskUsagePercent,
                healthyServices,
                totalServices,
                serverDetails: serverDetails ?? {},
                djangoDetails,
                serviceStatus: serviceStatusMap,
                processInfo: processInfo ?? {},
                systemLoad: systemLoad ?? {},
                memoryDetails,
                diskDetails,
            };
        } catch (error) {
            console.warn('Error processing health data:', error);
            return null;
        }
    }, [healthData, healthError]);

    // Handlers
    const handleReload = useCallback(() => window.location.reload(), []);
    const handleRemindMeLater = useCallback(() => {
        closeUpdateModal();
        setUpdateModalView('updates');
        localStorage.setItem(UPDATE_REMINDER_STORAGE_KEY, Date.now().toString());
    }, [closeUpdateModal]);
    const handleCheckForUpdates = useCallback(() => refetchFrontendVersion(), [refetchFrontendVersion]);


    useEffect(() => {
        if (healthData && !healthError && updateInfo.isAvailable) {
            const lastReminder = localStorage.getItem(UPDATE_REMINDER_STORAGE_KEY);
            if (!lastReminder || (Date.now() - parseInt(lastReminder)) >= UPDATE_REMINDER_DELAY) {
                openUpdateModal();
            }
        }
    }, [healthData, healthError, updateInfo.isAvailable, openUpdateModal]);

    // Helper functions
    const getStatusColor = (status?: string | null) => {
        switch (status?.toLowerCase()) {
            case 'healthy': case 'ok': case 'online': return 'green';
            case 'warning': return 'yellow';
            case 'error': case 'offline': return 'red';
            default: return 'gray';
        }
    };

    const getUsageColor = (percent: number) => percent >= 90 ? 'red' : percent >= 75 ? 'yellow' : 'green';

    const isDev = window.location.hostname.toLowerCase() !== 'meridian.ociuzerp.in';

    const profileAvatar = (
        <Avatar
            src={userAvatarUrl || undefined}
            size="sm"
            radius="xl"
            color="blue"
            className="shrink-0"
        >
            {((authState?.user?.firstName?.[0] || '') + (authState?.user?.lastName?.[0] || '')).toUpperCase() || 'U'}
        </Avatar>
    );

    // Runs the action and closes the mobile profile sheet
    const sheetAction = (action: () => void) => () => {
        closeProfileSheet();
        action();
    };

    // Plain icon in a fixed-width column, like the desktop account menu — no
    // boxed tiles — so every row's icon and label line up.
    const sheetIconTile = (icon: React.ReactNode) => (
        <span
            className="flex items-center justify-center shrink-0"
            style={{ width: 20, color: 'var(--app-text-muted)' }}
        >
            {icon}
        </span>
    );

    const sheetItem = (label: string, icon: React.ReactNode, onClick: () => void) => (
        <UnstyledButton
            onClick={onClick}
            className="w-full flex items-center active:opacity-70"
            style={{ color: 'var(--app-text)', minHeight: 48, gap: 14, padding: '0 14px' }}
        >
            {sheetIconTile(icon)}
            {/* No `inherit`: it pulled in the button's larger font, so these two rows
                rendered bigger than "Sound Alerts". */}
            <Text size="sm" fw={500} className="flex-1" style={{ color: 'var(--app-text)' }}>{label}</Text>
            <IconChevronRight size={16} style={{ color: 'var(--app-text-muted)' }} />
        </UnstyledButton>
    );


    return (
        <>
            <AppShell
                header={{ height: 40 }}
                navbar={{
                    width: 200,
                    breakpoint: 'sm',
                    // Mobile navigates via the bottom nav, so the sidebar never opens there.
                    collapsed: { mobile: true, desktop: !desktopOpened },
                }}
                padding="0"
                className="app-shell"
            >
                <AppShell.Header className="border-b shadow-sm" style={{ backgroundColor: 'var(--app-surface)', borderColor: 'var(--app-border)' }}>
                    <Group justify="space-between" h="100%" px="md" wrap="nowrap" style={{ overflow: 'hidden', paddingLeft: 'clamp(0.5rem, 2vw, 1rem)', paddingRight: 'clamp(0.5rem, 2vw, 1rem)' }}>
                        {/* Left Section */}
                        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <Burger opened={desktopOpened} onClick={toggleDesktop} visibleFrom="sm" size="sm" style={{ color: 'var(--app-text-muted)' }} className="shrink-0" />
                            <Group gap="xs" wrap="nowrap" className="min-w-0" style={{ flexShrink: 0 }}>
                                <div className="h-8 w-8 text-white rounded-md flex items-center justify-center text-sm font-bold shrink-0" style={{ backgroundColor: 'var(--mantine-color-brand-6)' }}>
                                    {companyName?.charAt(0)}
                                </div>
                                <div className="min-w-0 hidden sm:block" style={{ maxWidth: '200px' }}>
                                    <Text className="font-semibold truncate" size="sm" style={{ color: 'var(--app-text)' }}>
                                        {companyName}
                                    </Text>
                                    {isDev && (
                                        <Text size="xs" c="dimmed" className="truncate">
                                            Development
                                        </Text>
                                    )}
                                </div>
                            </Group>
                            {/* Mobile: the current page's title, between the logo and the profile. */}
                            {isMobile && pageTitle && (
                                <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                                    {pageTitleIcon && createElement(pageTitleIcon, { size: 16, style: { color: 'var(--mantine-color-brand-6)', flexShrink: 0 } })}
                                    <Text size="sm" fw={700} className="truncate" style={{ color: 'var(--app-text)', minWidth: 0 }}>
                                        {pageTitle}
                                    </Text>
                                </Group>
                            )}
                            <Divider orientation="vertical" className="hidden lg:block" style={{ flexShrink: 0 }} />
                            <div className="hidden lg:block min-w-0" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                <Breadcrumbs />
                            </div>
                        </Group>

                        {/* Right Section - Profile trigger (dropdown on desktop, bottom sheet on mobile) */}
                        <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
                            <Menu shadow="md" width={240} position="bottom-end" withArrow disabled={isMobile}>
                                <Menu.Target>
                                    <UnstyledButton
                                        onClick={isMobile ? openProfileSheet : undefined}
                                        aria-label="Account menu"
                                        className="min-w-0 max-w-[120px] sm:max-w-[150px] md:max-w-[180px] lg:max-w-[200px]"
                                    >
                                        <Group gap="xs" wrap="nowrap" className="min-w-0">
                                            {profileAvatar}
                                            <div className="text-left min-w-0 hidden sm:block" style={{ maxWidth: '100%' }}>
                                                <Text size="xs" fw={600} className="truncate" style={{ color: 'var(--app-text)', display: 'block' }}>
                                                    {authState?.user?.firstName} {authState?.user?.lastName}
                                                </Text>
                                                <Text size="xs" c="dimmed" className="truncate" style={{ display: 'block' }}>
                                                    {authState?.user?.email}
                                                </Text>
                                            </div>
                                        </Group>
                                    </UnstyledButton>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <Menu.Label>Account</Menu.Label>
                                    <Menu.Item disabled>
                                        <div style={{ maxWidth: '200px', overflow: 'hidden' }}>
                                            <Text size="xs" fw={600} className="truncate" style={{ color: 'var(--app-text)', display: 'block' }}>
                                                {authState?.user?.firstName} {authState?.user?.lastName}
                                            </Text>
                                            <Text size="xs" c="dimmed" className="truncate" style={{ display: 'block' }}>
                                                {authState?.user?.email}
                                            </Text>
                                            <Text size="xs" c="dimmed" className="truncate" style={{ display: 'block' }}>
                                                {authState?.user?.roleDescription}
                                            </Text>
                                            <Text size="xs" c="dimmed" className="truncate" style={{ display: 'block' }}>
                                                {authState?.user?.company}
                                            </Text>

                                        </div>
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Label>Actions</Menu.Label>
                                    <Menu.Item
                                        leftSection={<IconUser size={16} stroke={1.8} />}
                                        onClick={openProfileModal}
                                    >
                                        Edit Profile
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<IconRefresh size={16} stroke={1.8} />}
                                        onClick={openUpdateModal}
                                    >
                                        System Status
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Label>Sound Preferences</Menu.Label>
                                    <Menu.Item
                                        leftSection={soundEnabled ? <IconVolume size={16} stroke={1.8} /> : <IconVolumeOff size={16} stroke={1.8} />}
                                        onClick={toggleSound}
                                    >
                                        {soundEnabled ? 'Mute Sound Alerts' : 'Unmute Sound Alerts'}
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Label>Theme</Menu.Label>
                                    <Menu.Item
                                        leftSection={<IconSun size={16} stroke={1.8} />}
                                        onClick={() => setColorScheme('light')}
                                    >
                                        Light Mode
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<IconMoon size={16} stroke={1.8} />}
                                        onClick={() => setColorScheme('dark')}
                                    >
                                        Dark Mode
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<IconDeviceDesktop size={16} stroke={1.8} />}
                                        onClick={() => setColorScheme('auto')}
                                    >
                                        System
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Item
                                        onClick={logout}
                                        leftSection={<IconLogout size={16} stroke={1.8} />}
                                        color="red"
                                    >
                                        Sign Out
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        </Group>
                    </Group>
                </AppShell.Header>

                <AppShell.Navbar p="md" className="border-r" style={{ borderColor: 'var(--app-border)', backgroundColor: 'var(--app-surface)' }}>
                    <Sidebar />
                </AppShell.Navbar>

                <AppShell.Main className="app-shell-main">
                    <Box className="app-page" style={{ height: '100%', overflow: 'hidden' }}>
                        <Box className="app-card" style={{ height: '100%' }}>{children}</Box>
                    </Box>
                </AppShell.Main>
            </AppShell>

            {/* Mobile Profile Bottom Sheet */}
            <Drawer
                opened={profileSheetOpened && isMobile}
                onClose={closeProfileSheet}
                position="bottom"
                withCloseButton={false}
                radius="lg"
                overlayProps={{ backgroundOpacity: 0.45, blur: 2 }}
                styles={{
                    // Pin the sheet to the bottom edge, full width. `flex: 0 0 auto`
                    // alone (needed so the height follows the content) also let the
                    // width shrink to the content, so the sheet floated narrower than
                    // the screen with a gap underneath.
                    inner: { padding: 0, alignItems: 'flex-end', justifyContent: 'stretch' },
                    // Size to content (Mantine's size="auto" stretched it full-screen, hiding the overlay)
                    content: {
                        flex: '0 0 auto',
                        width: '100%',
                        maxWidth: '100%',
                        height: 'auto',
                        maxHeight: '85dvh',
                        marginTop: 'auto',
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        backgroundColor: 'var(--app-surface)',
                    },
                    body: { padding: '0 16px calc(16px + env(safe-area-inset-bottom))' },
                }}
            >
                {/* Grab handle: tap or swipe down to close */}
                {/* data-autofocus: Mantine focuses the first focusable element on open,
                    which put a focus ring on the close button. Focus the handle instead. */}
                <div
                    role="button"
                    aria-label="Close account menu"
                    tabIndex={-1}
                    data-autofocus
                    onClick={closeProfileSheet}
                    onTouchStart={(e) => { sheetTouchStartY.current = e.touches[0].clientY; }}
                    onTouchEnd={(e) => {
                        if (e.changedTouches[0].clientY - sheetTouchStartY.current > 40) closeProfileSheet();
                    }}
                    className="flex justify-center pt-2.5 pb-3 cursor-pointer outline-none"
                >
                    <div className="h-1 w-10 rounded-full" style={{ backgroundColor: 'var(--app-border)' }} />
                </div>

                {/* Profile header */}
                <Group gap={12} wrap="nowrap" mb={16} align="center">
                    <Avatar src={userAvatarUrl || undefined} size={48} radius="xl" color="blue">
                        {((authState?.user?.firstName?.[0] || '') + (authState?.user?.lastName?.[0] || '')).toUpperCase() || 'U'}
                    </Avatar>
                    <div className="min-w-0 flex-1">
                        <Text size="md" fw={700} className="truncate" style={{ color: 'var(--app-text)' }}>
                            {authState?.user?.firstName} {authState?.user?.lastName}
                        </Text>
                        <Text size="xs" c="dimmed" className="truncate">{authState?.user?.email}</Text>
                        {authState?.user?.roleDescription && (
                            <Badge size="xs" variant="light" mt={4} radius="sm">
                                {authState.user.roleDescription}
                            </Badge>
                        )}
                    </div>
                    <ActionIcon variant="subtle" color="gray" radius="xl" size={36} onClick={closeProfileSheet} aria-label="Close" style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
                        <IconX size={18} />
                    </ActionIcon>
                </Group>

                {/* Actions */}
                <Paper withBorder radius="md" mb={16} style={{ overflow: 'hidden', backgroundColor: 'transparent', borderColor: 'var(--app-border)' }}>
                    {sheetItem('Edit Profile', <IconUser size={19} stroke={1.8} />, sheetAction(openProfileModal))}
                    <Divider style={{ borderColor: 'var(--app-border)', marginLeft: 48 }} />
                    {sheetItem('System Status', <IconRefresh size={19} stroke={1.8} />, sheetAction(openUpdateModal))}
                    <Divider style={{ borderColor: 'var(--app-border)', marginLeft: 48 }} />
                    <Group justify="space-between" wrap="nowrap" style={{ minHeight: 48, padding: '0 14px' }}>
                        <Group gap={14} wrap="nowrap">
                            {sheetIconTile(soundEnabled ? <IconVolume size={19} stroke={1.8} /> : <IconVolumeOff size={19} stroke={1.8} />)}
                            <Text size="sm" fw={500} style={{ color: 'var(--app-text)' }}>Sound Alerts</Text>
                        </Group>
                        <Switch checked={soundEnabled} onChange={toggleSound} size="md" aria-label="Sound alerts" />
                    </Group>
                </Paper>

                {/* Theme */}
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={8} style={{ letterSpacing: 0.5 }}>Theme</Text>
                <SegmentedControl
                    fullWidth
                    size="sm"
                    radius="lg"
                    mb={16}
                    value={colorSchemePreference}
                    onChange={(value) => setColorScheme(value as 'light' | 'dark' | 'auto')}
                    data={[
                        { value: 'light', label: <Group gap={6} justify="center" wrap="nowrap"><IconSun size={15} /><span>Light</span></Group> },
                        { value: 'dark', label: <Group gap={6} justify="center" wrap="nowrap"><IconMoon size={15} /><span>Dark</span></Group> },
                        { value: 'auto', label: <Group gap={6} justify="center" wrap="nowrap"><IconDeviceDesktop size={15} /><span>System</span></Group> },
                    ]}
                />

                <Button
                    fullWidth
                    variant="light"
                    color="red"
                    radius="lg"
                    size="md"
                    leftSection={<IconLogout size={18} stroke={1.8} />}
                    onClick={sheetAction(logout)}
                >
                    Sign Out
                </Button>
            </Drawer>

            {/* Update Modal */}
            <Modal opened={updateModalOpened} onClose={handleRemindMeLater} title={
                <div className="flex items-center gap-2">
                    <div
                        className="p-2 rounded-lg"
                        style={{
                            backgroundColor: updateInfo.isAvailable
                                ? 'var(--app-primary-soft)'
                                : 'var(--mantine-color-green-1)',
                            color: updateInfo.isAvailable
                                ? 'var(--mantine-color-brand-7)'
                                : 'var(--mantine-color-green-6)',
                        }}
                    >
                        {updateInfo.isAvailable ? (
                            <IconRefresh className="w-4 h-4" stroke={1.8} />
                        ) : (
                            <IconCheck className="w-4 h-4" stroke={1.8} />
                        )}
                    </div>
                    <div>
                        <Text size="md" fw={600} c="gray.8">{updateInfo.isAvailable ? 'New Update Available' : 'System Up to Date'}</Text>
                        <Text size="xs" c="gray.6">{updateInfo.isAvailable ? 'Version update ready to install' : 'You have the latest version'}</Text>
                    </div>
                </div>
            } centered size="lg" >
                <Box className="space-y-4">
                    <div className="flex justify-center">
                        <SegmentedControl value={updateModalView} onChange={(value) => setUpdateModalView(value as 'updates' | 'system')} data={[
                            { label: 'Updates', value: 'updates' }, { label: 'System Details', value: 'system' }
                        ]} size="sm" radius="md" />
                    </div>

                    {updateModalView === 'updates' && (
                        <div className="space-y-4">
                            <Text c="gray.7" size="sm">
                                {updateInfo.isAvailable ? 'A new version of the application is available with the latest features and improvements.' : 'Your application is running the most recent version with all the latest features.'}
                            </Text>
                            {updateInfo.isAvailable && (
                                <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--app-primary-soft)', borderColor: 'var(--mantine-color-brand-3)' }}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <IconInfoCircle className="w-4 h-4" stroke={1.8} style={{ color: 'var(--mantine-color-brand-6)' }} />
                                        <Text size="sm" fw={500} c="brand.7">Version Information</Text>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Badge variant="light" color="gray" size="xs">v{updateInfo.currentVersion}</Badge>
                                        <IconX className="w-3 h-3" stroke={1.8} style={{ color: 'var(--app-text-muted)' }} />
                                        <Badge variant="filled" color="blue" size="xs">v{updateInfo.newVersion}</Badge>
                                    </div>
                                </div>
                            )}
                            <Group justify="flex-end" gap="sm">
                                {updateInfo.isAvailable && <Button variant="default" size="sm" onClick={handleRemindMeLater}>Remind Later</Button>}
                                <Button variant="light" size="sm" onClick={handleCheckForUpdates} loading={versionLoading}>Check Again</Button>
                                <Button size="sm" onClick={updateInfo.isAvailable ? handleReload : handleRemindMeLater} color={updateInfo.isAvailable ? 'blue' : 'gray'}>
                                    {updateInfo.isAvailable ? 'Update Now' : 'Close'}
                                </Button>
                            </Group>
                        </div>
                    )}

                    {updateModalView === 'system' && (
                        <div className="space-y-4">
                            <Text size="md" fw={600} c="gray.8">System Information</Text>

                            {healthLoading && (
                                <div className="flex items-center justify-center py-8">
                                    <div className="text-center">
                                        <IconRefresh className="w-8 h-8 animate-spin mx-auto mb-2" stroke={1.6} style={{ color: 'var(--app-text-muted)' }} />
                                        <Text size="sm" c="dimmed">Loading system information...</Text>
                                    </div>
                                </div>
                            )}

                            {healthError && (
                                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <IconCircleX className="w-5 h-5 text-red-500" stroke={1.8} />
                                        <Text size="sm" fw={500} c="red.7">Unable to load system information</Text>
                                    </div>
                                    <Text size="xs" c="red.6">The system health check is currently unavailable. Please try again later.</Text>
                                </div>
                            )}

                            {deploymentInfo && !healthLoading && !healthError && (
                                <>
                                    <SimpleGrid cols={2} spacing="sm">
                                        <SystemCard icon={IconDeviceDesktop} title="Environment" color="blue">
                                            <Stack align="center" gap="xs">
                                                <Badge color={deploymentInfo.isDevelopmentServer ? 'yellow' : 'green'} size="xs">
                                                    {deploymentInfo.isDevelopmentServer ? 'DEV' : 'PROD'}
                                                </Badge>
                                            </Stack>
                                        </SystemCard>
                                        <SystemCard icon={IconServer} title="Services" color="green">
                                            <Stack align="center" gap="xs">
                                                <Text fw={700} size="md">{deploymentInfo.healthyServices}/{deploymentInfo.totalServices}</Text>
                                            </Stack>
                                        </SystemCard>
                                    </SimpleGrid>
                                    <Grid gap="sm">
                                        <Grid.Col span={{ base: 12, md: 6 }}>
                                            <SystemCard icon={IconCpu} title="System Resources" color="orange">
                                                <SimpleGrid cols={2} spacing="md">
                                                    <Box ta="center">
                                                        <RingProgress size={80} thickness={8} sections={[{ value: deploymentInfo.memoryUsagePercent, color: getUsageColor(deploymentInfo.memoryUsagePercent) }]} label={<Text ta="center" size="sm" fw={700}>{deploymentInfo.memoryUsagePercent}%</Text>} />
                                                        <Text size="xs" c="dimmed" tt="uppercase" fw={600} mt="xs">Memory</Text>
                                                    </Box>
                                                    <Box ta="center">
                                                        <RingProgress size={80} thickness={8} sections={[{ value: deploymentInfo.diskUsagePercent, color: getUsageColor(deploymentInfo.diskUsagePercent) }]} label={<Text ta="center" size="sm" fw={700}>{deploymentInfo.diskUsagePercent}%</Text>} />
                                                        <Text size="xs" c="dimmed" tt="uppercase" fw={600} mt="xs">Disk</Text>
                                                    </Box>
                                                </SimpleGrid>
                                            </SystemCard>
                                        </Grid.Col>
                                        <Grid.Col span={{ base: 12, md: 6 }}>
                                            <SystemCard icon={IconWifi} title="Service Health" color="green">
                                                <Stack gap="xs">
                                                    {Object.entries(deploymentInfo.serviceStatus).map(([service, details]) => (
                                                        <Group key={service} justify="space-between" p="xs" bg="gray.0" style={{ borderRadius: '6px' }}>
                                                            <Group gap="xs">
                                                                {details?.status?.toLowerCase() === 'healthy' || details?.status?.toLowerCase() === 'ok'
                                                                    ? <IconCircleCheck className="w-4 h-4 text-green-500" stroke={1.8} />
                                                                    : <IconCircleX className="w-4 h-4 text-red-500" stroke={1.8} />
                                                                }
                                                                <Text size="sm" tt="capitalize" fw={600}>{service}</Text>
                                                            </Group>
                                                            <Badge color={getStatusColor(details?.status)} size="xs" variant="filled">{details?.status || 'Unknown'}</Badge>
                                                        </Group>
                                                    ))}
                                                </Stack>
                                            </SystemCard>
                                        </Grid.Col>
                                    </Grid>
                                </>
                            )}

                            <Group justify="flex-end" gap="sm">
                                <Button variant="light" size="sm" onClick={handleCheckForUpdates} loading={versionLoading}>Refresh Data</Button>
                                <Button size="sm" onClick={handleRemindMeLater} color="gray">Close</Button>
                            </Group>
                        </div>
                    )}
                </Box>
            </Modal>

            {/* Profile Edit Modal */}
            <Modal
                opened={profileModalOpened}
                onClose={closeProfileModal}
                title={
                    <Group gap="xs">
                        <div className="p-2 rounded-lg" style={{ display: 'flex', alignItems: 'center', backgroundColor: 'var(--app-primary-soft)', color: 'var(--mantine-color-brand-6)' }}>
                            <IconUser size={18} />
                        </div>
                        <div>
                            <Text size="md" fw={600} style={{ color: 'var(--app-text)' }}>Update Your Profile</Text>
                            <Text size="xs" c="dimmed">Edit your personal details and change your password</Text>
                        </div>
                    </Group>
                }
                centered
                size="md"
                closeOnClickOutside={!isIncompleteProfile}
                closeOnEscape={!isIncompleteProfile}
                withCloseButton={!isIncompleteProfile}
            >
                {authState.user && (
                    <ProfileEditForm
                        user={authState.user}
                        onSuccess={(updatedUser) => {
                            updateUserStore(updatedUser);
                        }}
                        onClose={closeProfileModal}
                        forceComplete={isIncompleteProfile}
                        onLogout={logout}
                    />
                )}
            </Modal>
        </>
    );
};

interface ProfileEditFormProps {
    user: any;
    onSuccess: (updatedUser: any) => void;
    onClose: () => void;
    forceComplete: boolean;
    onLogout: () => void;
}

const ProfileEditForm: FC<ProfileEditFormProps> = ({ user, onSuccess, onClose, forceComplete, onLogout }) => {
    const [firstName, setFirstName] = useState(user?.firstName || '');
    const [lastName, setLastName] = useState(user?.lastName || '');
    const [password, setPassword] = useState('');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [tempImageSrc, setTempImageSrc] = useState<string | null>(null);

    const baseFileUrl = useMemo(() => {
        return env.baseFileUrl.endsWith('/') ? env.baseFileUrl : `${env.baseFileUrl}/`;
    }, []);

    useEffect(() => {
        if (user?.fileImage) {
            setPreviewUrl(user.fileImage.startsWith('http') ? user.fileImage : baseFileUrl + user.fileImage);
        }
    }, [user, baseFileUrl]);

    const handleFileChange = (file: File | null) => {
        if (file) {
            setTempImageSrc(URL.createObjectURL(file));
        }
    };

    const handleClearImage = () => {
        setUploadedFile(null);
        setPreviewUrl(null);
    };

    const handleCropped = (blob: Blob) => {
        const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
        setUploadedFile(file);
        setPreviewUrl(URL.createObjectURL(blob));
        setTempImageSrc(null);
    };

    const handleCropCancel = () => {
        setTempImageSrc(null);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!firstName.trim() || !lastName.trim()) {
            handleNotification({ type: 'ERROR', message: 'First name and Last name are required' });
            return;
        }
        setIsSaving(true);
        try {
            const formData = new FormData();
            formData.append('firstName', firstName.trim());
            formData.append('lastName', lastName.trim());
            if (password) {
                formData.append('password', password);
            }
            if (uploadedFile) {
                formData.append('profilePic', uploadedFile);
            } else if (previewUrl === null) {
                formData.append('profilePic', '');
            }

            const data = await authService().updateProfile(formData);
            if (data && data.user) {
                onSuccess({ ...data.user, profileCompleted: true });
                handleNotification({ type: 'SUCCESS', message: 'Profile updated successfully!' });
                onClose();
            } else {
                throw new Error('Invalid server response');
            }
        } catch (error: any) {
            console.error('Profile update failed:', error);
            handleNotification({ type: 'ERROR', message: error.message || 'Failed to update profile' });
        } finally {
            setIsSaving(false);
        }
    };

    const avatarInitials = ((firstName?.[0] || '') + (lastName?.[0] || '')).toUpperCase() || 'U';

    if (tempImageSrc) {
        return (
            <CircularCropper
                imageSrc={tempImageSrc}
                onCrop={handleCropped}
                onCancel={handleCropCancel}
            />
        );
    }

    return (
        <form onSubmit={handleSave} style={{ position: 'relative' }}>
            <LoadingOverlay visible={isSaving} overlayProps={{ radius: 'sm', blur: 2 }} />
            <Stack gap="md">
                {forceComplete && (
                    <Paper p="sm" style={{ border: '1px solid var(--mantine-color-yellow-3)', borderRadius: '8px', backgroundColor: 'rgba(255, 192, 0, 0.08)' }}>
                        <Text size="xs" c="yellow.9" fw={600} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <IconInfoCircle size={14} /> Please complete your profile details to access the system dashboard.
                        </Text>
                    </Paper>
                )}

                {/* Avatar section */}
                <Group justify="center" align="center" gap="md" style={{ flexDirection: 'column' }}>
                    <div style={{ position: 'relative', width: 96, height: 96 }}>
                        <Avatar
                            src={previewUrl || undefined}
                            size={96}
                            radius="xl"
                            color="blue"
                            style={{
                                border: '3px solid var(--mantine-color-brand-6)',
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                            }}
                        >
                            {avatarInitials}
                        </Avatar>
                        {(previewUrl || uploadedFile) && (
                            <ActionIcon
                                variant="filled"
                                color="red"
                                size="xs"
                                onClick={handleClearImage}
                                style={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    zIndex: 10,
                                    borderRadius: '50%',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                }}
                                aria-label="Clear profile image"
                            >
                                <IconX size={12} />
                            </ActionIcon>
                        )}
                    </div>
                    <FileButton onChange={handleFileChange} accept="image/*">
                        {(props) => (
                            <Button {...props} variant="light" size="xs" leftSection={<IconUpload size={14} />} style={{ borderRadius: '20px' }}>
                                Change Picture
                            </Button>
                        )}
                    </FileButton>
                </Group>

                <Paper p="md" withBorder style={{ backgroundColor: 'var(--app-bg)', borderColor: 'var(--app-border)', borderRadius: '12px' }}>
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb="md" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <IconUser size={14} /> Personal Details
                    </Text>
                    
                    <Stack gap="sm">
                        <Group grow gap="sm">
                            <TextInput
                                label="First Name"
                                placeholder="Enter first name"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                leftSection={<IconUser size={16} style={{ color: 'var(--app-text-muted)' }} />}
                                required
                            />
                            <TextInput
                                label="Last Name"
                                placeholder="Enter last name"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                leftSection={<IconUser size={16} style={{ color: 'var(--app-text-muted)' }} />}
                                required
                            />
                        </Group>

                    </Stack>
                </Paper>

                <Paper p="md" withBorder style={{ backgroundColor: 'var(--app-bg)', borderColor: 'var(--app-border)', borderRadius: '12px' }}>
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb="md" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <IconLock size={14} /> Security
                    </Text>

                    {!showPassword ? (
                        <Button
                            variant="light"
                            size="sm"
                            leftSection={<IconKey size={14} />}
                            onClick={() => setShowPassword(true)}
                            style={{ borderRadius: '8px' }}
                        >
                            Change Password
                        </Button>
                    ) : (
                        <Stack gap="sm">
                            <PasswordInput
                                label="New Password"
                                placeholder="Enter new secure password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                leftSection={<IconLock size={16} style={{ color: 'var(--app-text-muted)' }} />}
                            />
                            <Button
                                variant="subtle"
                                size="xs"
                                color="gray"
                                onClick={() => {
                                    setShowPassword(false);
                                    setPassword('');
                                }}
                                style={{ alignSelf: 'flex-start' }}
                            >
                                Cancel Password Change
                            </Button>
                        </Stack>
                    )}
                </Paper>

                <Group justify="flex-end" gap="sm" mt="md">
                    {forceComplete ? (
                        <Button variant="outline" color="red" size="sm" onClick={onLogout} style={{ borderRadius: '8px' }}>
                            Sign Out
                        </Button>
                    ) : (
                        <Button variant="default" size="sm" onClick={onClose} style={{ borderRadius: '8px' }}>
                            Cancel
                        </Button>
                    )}
                    <Button type="submit" size="sm" color="blue" style={{ borderRadius: '8px' }}>
                        Save Profile
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};

interface CircularCropperProps {
    imageSrc: string;
    onCrop: (croppedBlob: Blob) => void;
    onCancel: () => void;
}

const CircularCropper: FC<CircularCropperProps> = ({ imageSrc, onCrop, onCancel }) => {
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        const containerWidth = 200;
        const containerHeight = 200;

        let width = img.naturalWidth;
        let height = img.naturalHeight;

        const ratio = width / height;

        if (ratio > 1) {
            height = containerHeight;
            width = containerHeight * ratio;
        } else {
            width = containerWidth;
            height = containerWidth / ratio;
        }

        setImgSize({ width, height });
        setOffset({
            x: (containerWidth - width) / 2,
            y: (containerHeight - height) / 2
        });
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        setOffset({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 1) {
            setIsDragging(true);
            setDragStart({
                x: e.touches[0].clientX - offset.x,
                y: e.touches[0].clientY - offset.y
            });
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || e.touches.length !== 1) return;
        setOffset({
            x: e.touches[0].clientX - dragStart.x,
            y: e.touches[0].clientY - dragStart.y
        });
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    const handleConfirm = () => {
        const image = imageRef.current;
        const container = containerRef.current;
        if (!image || !container) return;

        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 300;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.beginPath();
        ctx.arc(150, 150, 150, 0, Math.PI * 2);
        ctx.clip();

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 300, 300);

        const scale = 300 / 200;
        const w = imgSize.width * zoom * scale;
        const h = imgSize.height * zoom * scale;
        const x = offset.x * scale;
        const y = offset.y * scale;

        ctx.drawImage(image, x, y, w, h);

        canvas.toBlob((blob) => {
            if (blob) {
                onCrop(blob);
            }
        }, 'image/jpeg', 0.9);
    };

    return (
        <Stack align="center" gap="md" py="sm">
            <Text size="xs" c="dimmed" ta="center">Drag to adjust crop, use slider to zoom</Text>
            
            <div
                ref={containerRef}
                style={{
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: 'move',
                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45), inset 0 0 8px rgba(0,0,0,0.3)',
                    border: '3px solid var(--mantine-color-brand-6)',
                    backgroundColor: 'var(--app-surface-muted)',
                    touchAction: 'none',
                    margin: '10px 0'
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <img
                    ref={imageRef}
                    src={imageSrc}
                    alt="Source"
                    onLoad={handleImageLoad}
                    style={{
                        position: 'absolute',
                        transform: `scale(${zoom})`,
                        left: offset.x,
                        top: offset.y,
                        transformOrigin: '0 0',
                        maxWidth: 'none',
                        userSelect: 'none',
                        pointerEvents: 'none',
                        width: imgSize.width || 'auto',
                        height: imgSize.height || 'auto',
                    }}
                />
            </div>

            <Group gap="sm" style={{ width: '100%', maxWidth: 260 }} justify="center">
                <Text size="xs" style={{ color: 'var(--app-text-muted)' }}>Zoom</Text>
                <input
                    type="range"
                    min="1"
                    max="4"
                    step="0.05"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    style={{
                        flex: 1,
                        height: '4px',
                        borderRadius: '2px',
                        outline: 'none',
                        accentColor: 'var(--mantine-color-brand-6)',
                        cursor: 'pointer'
                    }}
                />
            </Group>

            <Group justify="flex-end" gap="xs" style={{ width: '100%' }} mt="md">
                <Button variant="default" size="sm" onClick={onCancel} style={{ borderRadius: '8px' }}>
                    Cancel
                </Button>
                <Button size="sm" color="blue" onClick={handleConfirm} style={{ borderRadius: '8px' }}>
                    Apply Crop
                </Button>
            </Group>
        </Stack>
    );
};

export default DashboardPage;